import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { Account, DashboardService } from '@treasury/domain/dashboard';
import { TmBaseComponent } from '@treasury/presentation';
import '@treasury/presentation/components/tm-bottom-sheet';
import '@treasury/presentation/components/tm-button';
import '@treasury/presentation/components/tm-footer';
import '@treasury/presentation/components/tm-list-header';
import { formatUsdCurrency } from '@treasury/utils';
import '@vaadin/grid';
import { css, html, nothing } from 'lit';
import { columnBodyRenderer } from 'lit-vaadin-helpers';
import { customElement, property, state } from 'lit/decorators.js';
import { type AccountGroup } from './configure-account-groups-sheet';
// eslint-disable-next-line no-duplicate-imports
import { TmSearchTermChangeEvent } from '@treasury/presentation/components/tm-list-header';
import { filterTableResults } from '../../../../utilities/table-result-filter';

export const tagName = 'available-accounts-sheet';

@customElement(tagName)
export class AvailableAccountsSheet extends TmBaseComponent {
    @property({ type: Object })
    public accountGroup?: AccountGroup;

    @property({ type: Boolean })
    public open = false;

    @state()
    private availableAccounts: Account[] = [];

    @state()
    private filteredAvailableAccounts: Account[] = [];

    @state()
    private selectedAccounts: Account[] = [];

    @state()
    private loading = false;

    @InjectProperty()
    declare private dashboardService: DashboardService;

    private get selectAll() {
        return this.selectedAccounts.length + (this.accountGroup?.accounts?.length ?? 0) === 50;
    }

    private get allSelected() {
        return this.selectedAccounts.length === this.availableAccounts.length;
    }

    private get selectAllButtonText() {
        return this.selectAll || this.allSelected ? 'Deselect All' : 'Select All';
    }

    protected updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('open') && this.open) {
            this.loadAccounts();
        }
    }

    protected render() {
        return html`<tm-bottom-sheet
            horizontal
            .open=${this.open}
            @close=${() => {
                this.open = false;
                this.dispatchEvent(new CustomEvent('close'));
                this.selectedAccounts = [];
            }}
            >${this.renderSheetContent()}</tm-bottom-sheet
        >`;
    }

    private dispatchSelectionEvent() {
        this.dispatchEvent(
            new CustomEvent('selected-items-changed', {
                detail: this.selectedAccounts,
            })
        );
    }

    private filterResults(e: TmSearchTermChangeEvent) {
        this.filteredAvailableAccounts = filterTableResults(e, this.availableAccounts, [
            'name',
            'number',
        ]);
    }

    private async loadAccounts() {
        this.loading = true;
        this.availableAccounts = await this.dashboardService.getAvailableAccounts();
        this.selectedAccounts = [];
        this.filteredAvailableAccounts = this.availableAccounts;
        this.loading = false;
    }

    private renderAccountNameColumn(account: Account) {
        return html`<div class="flex items-center py-2 border-b">
            <div class="text-start flex flex-col items-start justify-center">
                <span class="text-sm font-medium">${account.name}</span>
                <span class="text-xs text-secondary">(${account.number})</span>
            </div>
        </div>`;
    }

    private renderAmountColumn(account: Account) {
        return html`<div class="text-end flex flex-col py-2 pr-3 border-b">
            <span class="text-sm font-medium"
                >${formatUsdCurrency(account.availableBalance || 0)}</span
            >
            <span class="text-xs text-secondary">Available</span>
        </div>`;
    }

    private renderSheetContent() {
        if (!this.accountGroup) {
            return nothing;
        }
        return html`<div slot="header-center" class="flex flex-col">
                <span>Add Accounts</span><span class="text-xs">${this.accountGroup.name}</span>
            </div>
            <tm-section>
                <tm-list-header
                    @search-term-change=${(e: TmSearchTermChangeEvent) => this.filterResults(e)}
                ></tm-list-header>
                ${this.loading
                    ? html`<tm-loader card></tm-loader>`
                    : html`<vaadin-grid
                          all-rows-visible
                          .items=${this.availableAccounts}
                          .selectedItems=${this.selectedAccounts}
                          ><vaadin-grid-column
                              width="50px"
                              flex-grow="0"
                              ${columnBodyRenderer<Account>(
                                  item =>
                                      html`<div class="pl-3">
                                          <tm-checkbox
                                              aria-label="Select Row"
                                              ?checked=${this.selectedAccounts.includes(item)}
                                              .disabled=${!this.selectedAccounts.includes(item) &&
                                              this.selectedAccounts.length +
                                                  (this.accountGroup?.accounts?.length ?? 0) ===
                                                  50}
                                              @checked-changed=${(
                                                  e: CustomEvent<{ value: boolean }>
                                              ) => {
                                                  const checked = e.detail.value;
                                                  if (
                                                      checked &&
                                                      !this.selectedAccounts.includes(item)
                                                  ) {
                                                      this.selectedAccounts = [
                                                          ...this.selectedAccounts,
                                                          item,
                                                      ];
                                                  } else if (
                                                      !checked &&
                                                      this.selectedAccounts.includes(item)
                                                  ) {
                                                      this.selectedAccounts =
                                                          this.selectedAccounts.filter(
                                                              a => a !== item
                                                          );
                                                  }
                                                  this.dispatchSelectionEvent();
                                              }}
                                          ></tm-checkbox>
                                      </div>`,
                                  [this.selectAll]
                              )}
                          ></vaadin-grid-column
                          ><vaadin-grid-column
                              flex-grow="1"
                              auto-width
                              ${columnBodyRenderer(this.renderAccountNameColumn, [])}
                          ></vaadin-grid-column
                          ><vaadin-grid-column
                              flex-grow="0"
                              auto-width
                              text-align="end"
                              ${columnBodyRenderer(this.renderAmountColumn, [])}
                          ></vaadin-grid-column
                      ></vaadin-grid>`}</tm-section
            >
            <div
                class="flex justify-between px-4 py-2 border border-t bg-primary sticky bottom-[0px]"
            >
                <tm-button
                    importance="secondary"
                    .disabled=${this.availableAccounts.length === 0 || this.loading}
                    @click=${() => {
                        if (this.selectAll || this.allSelected) {
                            this.selectedAccounts = [];
                        } else {
                            const availableAccountsCount = this.availableAccounts.length;
                            const accountsInGroupCount = this.accountGroup?.accounts?.length ?? 0;
                            const maxAccounts = 50;
                            const accountsToAdd = Math.min(
                                maxAccounts - accountsInGroupCount,
                                availableAccountsCount
                            );
                            const newSelectedAccounts = this.availableAccounts.slice(
                                0,
                                accountsToAdd
                            );
                            this.selectedAccounts = newSelectedAccounts;
                        }
                        this.dispatchSelectionEvent();
                    }}
                    >${this.selectAllButtonText}</tm-button
                >
                <tm-button
                    importance="primary"
                    .disabled=${this.selectedAccounts.length === 0}
                    @click=${() => {
                        if (!this.accountGroup) {
                            return;
                        }
                        this.accountGroup.accounts = [
                            ...this.accountGroup.accounts,
                            ...this.selectedAccounts,
                        ];
                        this.dispatchEvent(
                            new CustomEvent('addAccountsToGroup', { detail: this.accountGroup })
                        );
                        this.loadAccounts();
                        this.open = false;
                    }}
                    >Add selected (${this.selectedAccounts.length})</tm-button
                >
            </div>`;
    }

    public static get styles() {
        return [
            css`
                vaadin-grid {
                    border: none;
                }
                vaadin-grid-cell-content {
                    box-sizing: border-box;
                    padding: 2px 0 0 0;
                }
                vaadin-grid::part(cell) {
                    border-top: none;
                    padding: 0;
                }
                vaadin-grid::part(selected-row-cell) {
                    background-color: var(--tm-color-gray-light-30);
                    background-image: none;
                    box-shadow: none;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: AvailableAccountsSheet;
    }
}
